import React, {Component} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {FormattedMessage, injectIntl} from 'react-intl';
import * as tracking from '../../tracking';
import {trackPage, unregisterPageInfo} from '../../tracking/amplitude/TrackingV2';
import Util from '../../forecast-app/shared/util/util';
import UpdateTicketingProfileMutation from '../../mutations/update_ticketing_profile_mutation';
import {createToast} from '../../forecast-app/shared/components/toasts/another-toast/toaster';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../forecast-app/shared/components/headers/top-header-bar/TopHeaderBar';
import SettingsProfileSubNav from '../settings/SettingsProfileSubNav';
import {isTicketingAvailable} from '../../forecast-app/shared/util/ticketingAvailability';
import {hasPermission} from '../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import {Redirect} from 'react-router-dom';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class TicketPreferences extends Component {
	constructor(props) {
		super(props);
		this.state = {
			signature: props.viewer.ticketingEmailSignature ?? '',
			replyTo: props.viewer.ticketingReplyToEmail ?? '',
			replyToError: false,
		};
		this.superPropertyChecksum = trackPage('Ticket Preferences');
	}

	componentDidMount() {
		document.title = 'Ticket Preferences - My profile - Forecast';
		tracking.trackPage('settings-ticket-preferences');
	}

	componentWillUnmount() {
		unregisterPageInfo(this.superPropertyChecksum);
	}

	showSaved() {
		createToast({message: this.props.intl.formatMessage({id: 'common.settings_saved'}), duration: 3000});
	}

	saveSignature() {
		// An emptied field is sent as null (not '') so clearing the signature
		// clears it server-side the same way clearing the Reply-To does.
		const value = this.state.signature;
		Util.CommitMutation(UpdateTicketingProfileMutation, {emailSignature: value === '' ? null : value}, () =>
			this.showSaved()
		);
	}

	saveReplyTo() {
		const value = this.state.replyTo.trim();
		if (value !== '' && !EMAIL_RE.test(value)) {
			this.setState({replyToError: true});
			return;
		}
		// The trimmed value is written back so the field shows exactly what was
		// saved rather than the untrimmed text the user typed.
		this.setState({replyTo: value, replyToError: false});
		Util.CommitMutation(UpdateTicketingProfileMutation, {replyToEmail: value === '' ? null : value}, () =>
			this.showSaved()
		);
	}

	render() {
		if (!isTicketingAvailable() || !hasPermission(PERMISSION_TYPE.TICKETS_VIEW)) {
			return <Redirect to="/my-profile/account-configuration" />;
		}
		const {formatMessage} = this.props.intl;
		return (
			<div className="section-content settings-app" data-cy="ticket-preferences-page">
				{this.props.children}
				<TopHeaderBarWrapper>
					<TopHeaderBar title={formatMessage({id: 'ticketing.profile.page_title'})} />
				</TopHeaderBarWrapper>
				<SettingsProfileSubNav viewer={this.props.viewer} />
				<div className="section-body">
					<div className="inner">
						<fieldset className="general">
							<h3>
								<FormattedMessage id="ticketing.profile.signature_heading" />
							</h3>
							<div className="wrapper">
								<label className="label">{formatMessage({id: 'ticketing.profile.signature_label'})}</label>
								<div className="input-wrapper">
									<textarea
										data-testid="signature-input"
										className="input"
										placeholder={formatMessage({id: 'ticketing.profile.signature_placeholder'})}
										value={this.state.signature}
										onChange={e => this.setState({signature: e.target.value})}
									/>
								</div>
								<span className="bar" />
							</div>
							<div className="save">
								<button
									className="submit-button"
									data-testid="save-signature"
									onClick={() => this.saveSignature()}
								>
									<FormattedMessage id="common.save" />
								</button>
							</div>
						</fieldset>
						<fieldset className="general">
							<h3>
								<FormattedMessage id="ticketing.profile.reply_to_heading" />
							</h3>
							<div className={this.state.replyToError ? 'wrapper-error' : 'wrapper'}>
								<label className="label">{formatMessage({id: 'ticketing.profile.reply_to_label'})}</label>
								<div className="input-wrapper">
									<input
										data-testid="reply-to-input"
										type="text"
										className="input"
										placeholder={formatMessage({id: 'ticketing.profile.reply_to_placeholder'})}
										value={this.state.replyTo}
										onChange={e => this.setState({replyTo: e.target.value, replyToError: false})}
									/>
								</div>
								<span className="bar" />
								{this.state.replyToError && (
									<span className="error-message">
										{formatMessage({id: 'ticketing.profile.reply_to_invalid'})}
									</span>
								)}
							</div>
							<div className="save">
								<button
									className="submit-button"
									data-testid="save-reply-to"
									onClick={() => this.saveReplyTo()}
								>
									<FormattedMessage id="common.save" />
								</button>
							</div>
						</fieldset>
					</div>
				</div>
			</div>
		);
	}
}

export {TicketPreferences as TicketPreferencesComponent};

const ticketPreferencesQuery = graphql`
	query ticketPreferences_Query {
		viewer {
			actualPersonId
			...ticketPreferences_viewer
		}
	}
`;

export {ticketPreferencesQuery};

export default createFragmentContainer(injectIntl(TicketPreferences), {
	viewer: graphql`
		fragment ticketPreferences_viewer on Viewer {
			id
			ticketingEmailSignature
			ticketingReplyToEmail
			...SettingsProfileSubNav_viewer
		}
	`,
});
